import type { RecienNacidoPendiente } from './services/recien-nacidos.service';

/** Marcas diacríticas Unicode, para comparar "Pérez" con "perez". */
const DIACRITICOS = /[̀-ͯ]/g;

/** Texto comparable: sin acentos, minúsculas y sin espacios de más. */
function norm(v: string | undefined | null): string {
  return (v ?? '')
    .normalize('NFD')
    .replace(DIACRITICOS, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** Solo dígitos: "32.979.494" y "32979494" son el mismo DNI. */
function digitos(v: string | undefined | null): string {
  return (v ?? '').replace(/\D+/g, '');
}

/**
 * Buscador de la pantalla Recién nacidos (VEN-581). MS recibe el pedido de la
 * mamá o el papá, así que el caso se identifica por el TITULAR (DNI, apellido,
 * nombre) o por el legajo; el bebé se busca por lo que ya tenga cargado
 * (apellido/nombre, DNI), que en muchos casos todavía es "BEBE" y sin documento.
 *
 * Un solo texto que matchea contra:
 *  - DNI del titular y DNI del recién nacido: por dígitos, con o sin puntos,
 *    coincidencia por prefijo (escribir "3297" ya filtra).
 *  - Número de legajo: con o sin el prefijo de letras ("GN044158" o "44158").
 *  - Apellido/nombre del titular y del recién nacido: sin acentos ni
 *    mayúsculas, coincidencia parcial y en cualquier orden de palabras
 *    ("ayelen mendez" encuentra a "MENDEZ MARIA AYELEN").
 */
export function coincideRecienNacido(rn: RecienNacidoPendiente, busqueda: string): boolean {
  const q = norm(busqueda);
  if (!q) return true;

  const qDigitos = digitos(q);
  const esNumerica = qDigitos.length > 0 && qDigitos === q.replace(/[.\s-]/g, '');
  if (esNumerica) {
    const dnis = [digitos(rn.documento), digitos(rn.titular?.documento)];
    if (dnis.some((d) => d.length > 0 && d.startsWith(qDigitos))) return true;
    return digitos(rn.legajo).startsWith(qDigitos);
  }

  // Legajo con prefijo de letras: "gn044158", "gn 44158".
  const legajo = norm(rn.legajo);
  if (legajo && legajo.replace(/\s+/g, '').startsWith(q.replace(/\s+/g, ''))) return true;

  const nombres = [
    norm(`${rn.titular?.apellSolt ?? ''} ${rn.titular?.nombres ?? ''}`),
    norm(`${rn.apellSolt} ${rn.nombres}`),
  ];
  const palabras = q.split(' ');
  return nombres.some((n) => n.length > 0 && palabras.every((p) => n.includes(p)));
}

/** Aplica la búsqueda manteniendo el orden de entrada. */
export function filtrarRecienNacidos(
  items: RecienNacidoPendiente[],
  busqueda: string,
): RecienNacidoPendiente[] {
  if (!norm(busqueda)) return items;
  return items.filter((rn) => coincideRecienNacido(rn, busqueda));
}
